import React, {useCallback, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import moment from 'moment'
import {
  createECommerceLeadComment,
  getECommerceLeadComments,
  updateECommerceLead,
} from '../../api/e-commerce'
import notifyToast from '../../components/Toast/Toast'
import {ECommerceLead, ECommerceLeadComment, ECommerceOfferStatus} from '../../models/e-commerce'

type Props = {
  show: boolean
  lead: ECommerceLead | null
  onClose: () => void
  onStatusUpdated?: (status: ECommerceOfferStatus | null) => void
  embedded?: boolean
  commentsOnly?: boolean
}

const offerStatuses: Array<{value: ECommerceOfferStatus; label: string}> = [
  {value: 'NEW', label: 'Novi lead'},
  {value: 'CONTACTED', label: 'Kontaktiran'},
  {value: 'WAITING', label: 'Čekamo odgovor'},
  {value: 'QUALIFIED', label: 'Kvalifikovan'},
  {value: 'PREPARING_OFFER', label: 'Priprema ponude'},
  {value: 'OFFER_SENT', label: 'Ponuda poslana'},
  {value: 'NEGOTIATION', label: 'Pregovori'},
  {value: 'WON', label: 'Dobijen posao'},
  {value: 'LOST', label: 'Izgubljen posao'},
  {value: 'NOT_RELEVANT', label: 'Nije relevantno'},
]

const getErrorMessage = (error: any, fallback: string) => {
  const status = error?.response?.status
  const validationMessage =
    error?.response?.data?.errors?.comment ?? error?.response?.data?.errors?.offer_status

  if (Array.isArray(validationMessage) && validationMessage.length > 0) {
    return validationMessage[0]
  }

  if (typeof validationMessage === 'string') {
    return validationMessage
  }

  if (status === 401) return 'Vaša sesija je istekla. Prijavite se ponovo.'
  if (status === 403) return 'Nemate dozvolu za ovu akciju.'
  if (status === 404) return 'Lead ili komentari nisu pronađeni.'
  if (status === 422) return 'Podaci nisu validni.'
  if (status >= 500) return 'Greška servera. Pokušajte ponovo.'

  return typeof error?.response?.data?.message === 'string' ? error.response.data.message : fallback
}

const formatCommentDate = (value: string) => {
  const date = moment(value)
  return date.isValid() ? date.format('DD-MM-YYYY HH:mm') : value
}

const LeadCommentsModal: React.FC<Props> = ({
  show,
  lead,
  onClose,
  onStatusUpdated,
  embedded = false,
  commentsOnly = false,
}) => {
  const [comments, setComments] = useState<ECommerceLeadComment[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [commentText, setCommentText] = useState('')
  const [offerStatus, setOfferStatus] = useState<ECommerceOfferStatus | ''>('')
  const [savingStatus, setSavingStatus] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  const loadComments = useCallback(
    async (page: number, append = false) => {
      if (!lead?.id) return

      try {
        if (append) {
          setLoadingOlder(true)
        } else {
          setLoading(true)
          setError(null)
        }

        const response = await getECommerceLeadComments(lead.id, page)
        const responseData = response.data
        const pageComments = Array.isArray(responseData.data) ? responseData.data : []
        const meta = responseData.meta
        const pageNumber = Number(meta?.current_page ?? page)
        const perPage = Number(meta?.per_page ?? 25)
        const total = Number(meta?.total ?? pageComments.length)
        const nextPageAvailable = Boolean(responseData.links?.next) || pageNumber * perPage < total

        setComments((currentComments) => (append ? [...currentComments, ...pageComments] : pageComments))
        setCurrentPage(pageNumber)
        setHasMore(nextPageAvailable)
      } catch (requestError) {
        const message = getErrorMessage(requestError, 'Komentari se nisu mogli učitati.')
        if (append) {
          notifyToast(false, message)
        } else {
          setError(message)
        }
      } finally {
        if (append) {
          setLoadingOlder(false)
        } else {
          setLoading(false)
        }
      }
    },
    [lead?.id]
  )

  useEffect(() => {
    if (!show || !lead?.id) return

    setComments([])
    setCurrentPage(1)
    setHasMore(false)
    setCommentText('')
    setOfferStatus((lead.offer_status as ECommerceOfferStatus) || '')
    setSubmitError(null)
    setStatusError(null)
    void loadComments(1)
  }, [show, lead?.id, loadComments])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedComment = commentText.trim()

    if (!trimmedComment) {
      setSubmitError('Komentar ne može biti prazan.')
      return
    }

    if (!lead?.id) return

    try {
      setSubmitting(true)
      setSubmitError(null)
      const response = await createECommerceLeadComment(lead.id, trimmedComment)
      const createdComment = response.data?.data

      if (createdComment) {
        setComments((currentComments) => [createdComment, ...currentComments])
      }

      setCommentText('')
      notifyToast(true, 'Komentar je uspješno dodat.')
    } catch (requestError) {
      setSubmitError(getErrorMessage(requestError, 'Komentar se nije mogao dodati.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleStatusSave = async () => {
    if (!lead?.id || !offerStatus) {
      setStatusError('Izaberite status ponude.')
      return
    }

    try {
      setSavingStatus(true)
      setStatusError(null)
      await updateECommerceLead(lead.id, {offer_status: offerStatus})
      onStatusUpdated?.(offerStatus)
      notifyToast(true, 'Status ponude je uspješno sačuvan.')
    } catch (requestError) {
      setStatusError(getErrorMessage(requestError, 'Status ponude se nije mogao sačuvati.'))
    } finally {
      setSavingStatus(false)
    }
  }

  const companyName = lead?.company_name || lead?.official_company_name || 'Lead'

  const content = (
    <>
      {!commentsOnly && (
        <section className='mb-8'>
          <h5 className='mb-4'>Dodaj komentar</h5>
          <form onSubmit={handleSubmit}>
            <textarea
              className={`form-control ${submitError ? 'is-invalid' : ''}`}
              rows={4}
              value={commentText}
              onChange={(event) => {
                setCommentText(event.target.value)
                if (submitError) setSubmitError(null)
              }}
              placeholder='Napišite napomenu...'
              disabled={submitting}
            />
            {submitError && <div className='invalid-feedback d-block'>{submitError}</div>}
            <div className='d-flex justify-content-end mt-3'>
              <button type='submit' className='btn btn-primary' disabled={submitting || !commentText.trim()}>
                {submitting ? 'Dodavanje...' : 'Dodaj komentar'}
              </button>
            </div>
          </form>
        </section>
      )}

      {!commentsOnly && (
        <section className='border-top pt-5 mb-8'>
          <h5 className='mb-4'>Status ponude</h5>
          <div className='row align-items-end'>
            <div className='col-md-8'>
              <label htmlFor='lead-offer-status' className='form-label'>
                Status
              </label>
              <select
                id='lead-offer-status'
                className={`form-select ${statusError ? 'is-invalid' : ''}`}
                value={offerStatus}
                onChange={(event) => {
                  setOfferStatus(event.target.value as ECommerceOfferStatus)
                  if (statusError) setStatusError(null)
                }}
                disabled={savingStatus}
              >
                <option value=''>Izaberite status</option>
                {offerStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              {statusError && <div className='invalid-feedback d-block'>{statusError}</div>}
            </div>
            <div className='col-md-4 mt-3 mt-md-0'>
              <button
                type='button'
                className='btn btn-primary w-100'
                onClick={() => void handleStatusSave()}
                disabled={savingStatus || !offerStatus}
              >
                {savingStatus ? 'Čuvanje...' : 'Sačuvaj status'}
              </button>
            </div>
          </div>
        </section>
      )}

      <section>
        {!commentsOnly && <h5 className='mb-4'>Historija komentara</h5>}
          {loading && (
            <div className='text-center py-8'>
              <span className='spinner-border text-primary' role='status' aria-label='Loading comments' />
            </div>
          )}

          {!loading && error && <div className='alert alert-danger'>{error}</div>}

          {!loading && !error && comments.length === 0 && (
            <div className='text-muted py-4'>Nema komentara.</div>
          )}

          {!loading && !error && comments.length > 0 && (
            <div className='d-flex flex-column gap-4'>
              {comments.map((item) => {
                const edited =
                  item.created_at &&
                  item.updated_at &&
                  moment(item.created_at).valueOf() !== moment(item.updated_at).valueOf()

                return (
                  <div key={item.id} className='border rounded p-4'>
                    <div className='d-flex justify-content-between align-items-start gap-3 mb-2'>
                      <div className='fw-bold'>{item.user?.name || 'Nepoznati korisnik'}</div>
                      <div className='text-muted fs-7 text-nowrap'>
                        {formatCommentDate(item.created_at)}
                        {edited ? ' · Izmijenjeno' : ''}
                      </div>
                    </div>
                    <div className='text-gray-700' style={{whiteSpace: 'pre-wrap'}}>
                      {item.comment}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {!loading && !error && hasMore && (
            <div className='text-center mt-5'>
              <button
                type='button'
                className='btn btn-light-primary'
                disabled={loadingOlder}
                onClick={() => void loadComments(currentPage + 1, true)}
              >
                {loadingOlder ? 'Učitavanje...' : 'Učitaj starije komentare'}
              </button>
            </div>
          )}
        </section>
    </>
  )

  if (embedded) {
    return <div>{content}</div>
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' dialogClassName='modal-dialog-scrollable'>
      <Modal.Header closeButton>
        <Modal.Title>Komentari — {companyName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{content}</Modal.Body>
    </Modal>
  )
}

export default LeadCommentsModal
